import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { LogicielCaisseEntrepotService } from 'app/services/logiciel-caisse/logiciel-caisse-entrepot.service';
import {
  EtablissementRow,
  LogicielCaisseParametreAssociationsService,
} from 'app/services/logiciel-caisse/logiciel-caisse-parametre-associations.service';
import {
  LogicielCaisseStockGestionService,
  StockLigneParEntrepot,
} from 'app/services/logiciel-caisse/logiciel-caisse-stock-gestion.service';
import { LogicielCaisseGestionStockStateService } from 'app/services/logiciel-caisse/logiciel-caisse-gestion-stock-state.service';
import { LogicielCaisseProduitService } from 'app/services/logiciel-caisse/logiciel-caisse-produit.service';
import { LogicielCaisseStockToolbarComponent } from '../shared/logiciel-caisse-stock-toolbar/logiciel-caisse-stock-toolbar.component';
import { LcStockListFilters } from '../shared/logiciel-caisse-stock-list.types';
import { GestionStockTab } from './logiciel-caisse-gestion-stock.component';

type StockTableRow = Record<string, unknown>;

@Component({
  selector: 'app-logiciel-caisse-gestion-stock-content',
  templateUrl: './logiciel-caisse-gestion-stock-content.component.html',
  styleUrls: ['./logiciel-caisse-gestion-stock-content.component.scss'],
})
export class LogicielCaisseGestionStockContentComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() tab: GestionStockTab = 'actif';
  @ViewChild('toolbar') toolbarCmp?: LogicielCaisseStockToolbarComponent;

  tableRows: StockTableRow[] = [];
  loading = false;

  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  serverTotal = 0;

  stockDetailOpen = false;
  stockDetailLoading = false;
  stockDetailTitle = '';
  stockDetailLines: StockLigneParEntrepot[] = [];

  private filters: LcStockListFilters = {
    searchTerm: '',
    establishmentIds: [],
    warehouseIds: [],
    effectiveWarehouseIds: [],
  };
  private expandedIndexes = new Set<number>();
  private parentRowClickTimer?: ReturnType<typeof setTimeout>;
  private readonly rowClickDelayMs = 280;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: LogicielCaisseAuthService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly associationsService: LogicielCaisseParametreAssociationsService,
    private readonly stockService: LogicielCaisseStockGestionService,
    private readonly produitService: LogicielCaisseProduitService,
    private readonly stockState: LogicielCaisseGestionStockStateService,
    private readonly toastr: ToastrService
  ) {}

  ngAfterViewInit(): void {
    this.loadFilterOptions();
    this.refreshFeatherIcons();
  }

  ngOnDestroy(): void {
    this.cancelPendingParentRowClick();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tab'] && !changes['tab'].firstChange) {
      this.currentPage = 1;
      this.expandedIndexes.clear();
      this.loadPage();
    }
  }

  onFiltersChange(filters: LcStockListFilters): void {
    this.filters = filters;
    this.currentPage = 1;
    this.expandedIndexes.clear();
    this.loadPage();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.expandedIndexes.clear();
    this.loadPage();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.expandedIndexes.clear();
    this.loadPage();
  }

  cell(row: StockTableRow, key: string): string {
    const v = row[key];
    return v == null || v === '' ? '—' : String(v);
  }

  hasChildren(row: StockTableRow): boolean {
    const children = row['children'];
    return Array.isArray(children) && children.length > 0;
  }

  childRows(row: StockTableRow): StockTableRow[] {
    const children = row['children'];
    return Array.isArray(children) ? (children as StockTableRow[]) : [];
  }

  isExpanded(index: number): boolean {
    return this.expandedIndexes.has(index);
  }

  toggleExpand(event: Event, index: number, row: StockTableRow): void {
    event.stopPropagation();
    this.toggleExpandAt(index, row);
  }

  onParentRowClick(event: Event, index: number, row: StockTableRow): void {
    if (!this.hasChildren(row) || this.isInteractiveRowTarget(event)) {
      return;
    }
    this.cancelPendingParentRowClick();
    this.parentRowClickTimer = setTimeout(() => {
      this.parentRowClickTimer = undefined;
      this.toggleExpandAt(index, row);
    }, this.rowClickDelayMs);
  }

  onChildRowDblClick(event: Event, child: StockTableRow): void {
    event.preventDefault();
    if (this.isInteractiveRowTarget(event)) {
      return;
    }
    this.openStockDetail(child);
  }

  private isInteractiveRowTarget(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return !!target?.closest('button, a, input, select, textarea, .chevron');
  }

  private cancelPendingParentRowClick(): void {
    if (this.parentRowClickTimer) {
      clearTimeout(this.parentRowClickTimer);
      this.parentRowClickTimer = undefined;
    }
  }

  private toggleExpandAt(index: number, row: StockTableRow): void {
    if (!this.hasChildren(row)) {
      return;
    }
    if (this.expandedIndexes.has(index)) {
      this.expandedIndexes.delete(index);
    } else {
      this.expandedIndexes.add(index);
    }
    this.refreshFeatherIcons();
  }

  stockQtyBadgeClasses(val: unknown): string[] {
    const n = this.parseStockQtyDisplay(val);
    if (n === null) {
      return [];
    }
    if (n <= 0) {
      return ['badge-rupture'];
    }
    if (n < 10) {
      return ['badge-faible'];
    }
    return ['badge-stock'];
  }

  openStockDetail(row: StockTableRow): void {
    const ids = this.collectProduitIds(row);
    if (ids.length === 0) {
      this.toastr.error('Produit introuvable pour ce détail.');
      return;
    }
    this.stockDetailTitle = this.rowDesignationTitle(row);
    this.stockDetailOpen = true;
    this.stockDetailLoading = true;
    this.stockDetailLines = [];
    const entIds = (this.filters.effectiveWarehouseIds || []).filter(
      (n) => Number.isFinite(n) && n > 0
    );
    this.stockService
      .getStockDetailParEntrepot(ids, entIds.length > 0 ? entIds : undefined)
      .subscribe({
        next: (lines) => {
          this.stockDetailLines = lines;
          this.stockDetailLoading = false;
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
          this.stockDetailLoading = false;
          this.stockDetailOpen = false;
        },
      });
  }

  closeStockDetail(): void {
    this.stockDetailOpen = false;
  }

  formatStockQty(v: number): string {
    return this.produitService.formatStockQty(v);
  }

  get stockDetailTotalPhysique(): number {
    return this.stockDetailLines.reduce((sum, line) => sum + (Number(line.stock_physique) || 0), 0);
  }

  get stockDetailTotalDisponible(): number {
    return this.stockDetailLines.reduce((sum, line) => sum + (Number(line.stock_disponible) || 0), 0);
  }

  private loadFilterOptions(): void {
    if (!this.authService.isAuthenticated()) {
      this.toolbarCmp?.setEstablishmentOptions([]);
      this.toolbarCmp?.setWarehouseOptions([]);
      this.loadPage();
      return;
    }

    const load$ = this.authService.isReadyForNavigation()
      ? of(true)
      : this.authService.bootstrap().pipe(catchError(() => of(false)));

    load$.pipe(
      switchMap(() =>
        forkJoin({
          entrepots: this.entrepotService.getEntrepotOptions().pipe(catchError(() => of([]))),
          etablissements: this.associationsService
            .listEtablissements()
            .pipe(catchError(() => of([] as EtablissementRow[]))),
          links: this.associationsService
            .listEtablissementEntrepotLinks()
            .pipe(catchError(() => of([]))),
        })
      )
    ).subscribe({
      next: ({ entrepots, etablissements, links }) => {
        const map = new Map<number, number[]>();
        for (const link of links || []) {
          const eid = Number(link.etablissement_id);
          const wid = Number(link.entrepot_id);
          if (!Number.isFinite(eid) || !Number.isFinite(wid) || eid <= 0 || wid <= 0) {
            continue;
          }
          const list = map.get(eid) ?? [];
          list.push(wid);
          map.set(eid, list);
        }

        this.toolbarCmp?.setEntrepotIdsByEtablissement(map);
        this.toolbarCmp?.setEstablishmentOptions(
          (Array.isArray(etablissements) ? etablissements : []).map((row) => ({
            id: Number(row.id),
            label: this.etablissementOptionLabel(row),
          }))
        );
        this.toolbarCmp?.setWarehouseOptions(
          (entrepots || []).map((w) => ({
            id: Number(w.id),
            label: w.label,
          }))
        );
        this.loadPage();
      },
      error: () => {
        this.toolbarCmp?.setEstablishmentOptions([]);
        this.toolbarCmp?.setWarehouseOptions([]);
        this.loadPage();
      },
    });
  }

  private etablissementOptionLabel(etab: EtablissementRow): string {
    const libelle = String(etab?.libelle || '').trim();
    const code = String(etab?.code_etablissement || '').trim();
    if (libelle && code) {
      return `${libelle} (${code})`;
    }
    return libelle || code || `Établissement #${etab?.id}`;
  }

  private loadPage(): void {
    this.loading = true;
    const entrepotIds = (this.filters.effectiveWarehouseIds || []).filter(
      (n) => Number.isFinite(n) && n > 0
    );
    // Établissement sélectionné sans entrepôt lié → aucun résultat (évite « pas de filtre »).
    const hasEstablishmentConstraint = (this.filters.establishmentIds || []).length > 0;
    const effectiveIds =
      hasEstablishmentConstraint && entrepotIds.length === 0 ? [-1] : entrepotIds;

    const actif = this.tab !== 'archive';

    forkJoin({
      counts: this.produitService.getProductsCounts(),
      list: this.stockService.getGestionGlobale({
        actif,
        page: this.currentPage,
        limit: this.pageSize,
        byGroup: true,
        entrepotIds: effectiveIds.length ? effectiveIds : undefined,
        searchRef: this.filters.searchTerm || undefined,
        stockPhysMin: this.filters.stockPhysMin,
        stockPhysMax: this.filters.stockPhysMax,
      }),
    }).subscribe({
      next: ({ counts, list }) => {
        this.stockState.setCounts(counts);
        this.tableRows = this.produitService.groupProduitsForTable(list.items);
        this.serverTotal = list.pagination.total;
        this.totalPages = list.pagination.totalPages;
        this.loading = false;
        this.refreshFeatherIcons();
      },
      error: (err: Error) => {
        this.toastr.error(err.message);
        if (this.tableRows.length === 0) {
          this.serverTotal = 0;
          this.totalPages = 1;
        }
        this.loading = false;
      },
    });
  }

  private collectProduitIds(row: StockTableRow): number[] {
    const children = row['children'];
    if (Array.isArray(children) && children.length > 0) {
      const ids = children
        .map((c) => Number((c as StockTableRow)['id']))
        .filter((n) => Number.isFinite(n));
      return [...new Set(ids)];
    }
    const id = Number(row['id']);
    return Number.isFinite(id) ? [id] : [];
  }

  private rowDesignationTitle(row: StockTableRow): string {
    const des = row['DESIGNATION'];
    if (typeof des === 'string') {
      const first = des.split('\n')[0]?.trim();
      if (first) {
        return first;
      }
    }
    const ref = row['REFERENCE'];
    return typeof ref === 'string' && ref.trim() !== '' ? ref.trim() : 'Produit';
  }

  private parseStockQtyDisplay(val: unknown): number | null {
    if (val == null || val === '—') {
      return null;
    }
    const s = String(val).replace(/\s/g, '').replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  private refreshFeatherIcons(): void {
    setTimeout(() => {
      try {
        const feather = (window as unknown as { feather?: { replace: () => void } }).feather;
        feather?.replace();
      } catch {
        /* noop */
      }
      this.cdr.markForCheck();
    }, 0);
  }
}
